import React, { useEffect, useState, useRef } from 'react';
import api from './services/api';
import { TerminalSquare, X } from 'lucide-react';
import './App.css';

interface Log {
    id: number;
    message: string;
    status: 'info' | 'success' | 'error' | 'warning';
    timestamp: string;
}

interface LogWindowProps {
    isOpen: boolean;
    onClose: () => void;
    connectionId: number | null;
    connectionTitle: string;
}

export const LogWindow: React.FC<LogWindowProps> = ({ isOpen, onClose, connectionId, connectionTitle }) => {
    const [logs, setLogs] = useState<Log[]>([]);
    const [loading, setLoading] = useState(false);
    const pollInterval = useRef<NodeJS.Timeout | null>(null);

    const fetchLogs = async () => {
        if (!connectionId) return;
        try {
            const response = await api.get(`/connections/${connectionId}/logs`);
            setLogs(response.data);
        } catch (error) {
            console.error("Erro ao buscar logs:", error);
        }
    };

    useEffect(() => {
        if (isOpen && connectionId) {
            setLoading(true);
            fetchLogs().finally(() => setLoading(false));

            // Start Polling
            pollInterval.current = setInterval(fetchLogs, 3000); // 3s refresh
        } else {
            setLogs([]);
            if (pollInterval.current) clearInterval(pollInterval.current);
        }

        return () => {
            if (pollInterval.current) clearInterval(pollInterval.current);
        };
    }, [isOpen, connectionId]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content glass-modal log-modal-content">
                <div className="modal-header">
                    <div className="header-title">
                        <div className="terminal-icon-wrapper">
                            <TerminalSquare size={18} className="text-accent" />
                        </div>
                        <div className="header-text">
                            <h3>Logs de Atividade</h3>
                            <p>{connectionTitle}</p>
                        </div>
                    </div>
                    <button className="close-btn-round" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="log-container">
                    <div className="log-terminal">
                        {logs.length === 0 && !loading && (
                            <div className="log-empty">
                                <p>Aguardando registros...</p>
                            </div>
                        )}

                        <div className="log-scroll">
                            {logs.map((log) => (
                                <div key={log.id} className={`log-row status-${log.status}`}>
                                    <span className="log-timestamp">
                                        [{new Date(log.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}]
                                    </span>
                                    <span className={`log-status-badge`}>
                                        {log.status.toUpperCase()}
                                    </span>
                                    <span className="log-content">{log.message}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="modal-footer log-footer">
                    <div className="auto-refresh-indicator">
                        <div className="pulse-dot"></div>
                        <span>Atualizando em tempo real</span>
                    </div>
                    <button className="btn-close-modal" onClick={onClose}>
                        Fechar Janela
                    </button>
                </div>
            </div>
        </div>
    );
}
